import { Link } from "react-router-dom";
import { AlertTriangle, CloudOff, RefreshCw } from "lucide-react";

import { counts } from "@/offline/outbox";
import { useOutbox } from "@/offline/useOutbox";

/**
 * The offline banner (Frontend Spec §25).
 *
 * Three things it must say, and nothing else: that the network is gone, that
 * nothing was lost, and — when something was refused — that a human has to look.
 *
 * It stays out of the way when everything is fine. A permanent "synchronisé"
 * strip is noise that teaches people to ignore the strip.
 */
export function OfflineBanner() {
  const { online, entries, state } = useOutbox();
  const { pending, conflict, failed } = counts(entries);
  const blocked = conflict + failed;

  if (online && pending === 0 && blocked === 0) return null;

  if (blocked > 0) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 border-b border-status-out/30 bg-danger-subtle px-3 py-1.5 text-[13px] text-ink md:px-4 desk:px-5"
      >
        <AlertTriangle className="size-4 shrink-0 text-status-out" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          {blocked === 1 ? "Une opération n'a pas pu être enregistrée" : `${blocked} opérations n'ont pas pu être enregistrées`}
          {conflict > 0 && failed > 0 ? " (conflit et échec)" : conflict > 0 ? " (conflit)" : ""}.
        </span>
        <Link to="/sync" className="shrink-0 font-medium text-brand hover:underline">
          Voir la file
        </Link>
      </div>
    );
  }

  if (!online) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 border-b border-warning/30 bg-warning-subtle px-3 py-1.5 text-[13px] text-ink md:px-4 desk:px-5"
      >
        <CloudOff className="size-4 shrink-0 text-warning" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          Hors ligne
          {pending > 0
            ? ` — ${pending} opération${pending > 1 ? "s" : ""} en attente, elles seront synchronisées.`
            : " — vos saisies seront synchronisées."}
        </span>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-border bg-surface-raised px-3 py-1.5 text-[13px] text-ink-muted md:px-4 desk:px-5"
    >
      <RefreshCw
        className={`size-4 shrink-0 ${state === "SYNCING" ? "animate-spin" : ""}`}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate">
        {state === "SYNCING" ? "Synchronisation…" : `${pending} opération(s) en attente de sync.`}
      </span>
      <Link to="/sync" className="shrink-0 text-brand hover:underline">
        Voir
      </Link>
    </div>
  );
}
